/**
 * Central buyer analytics utility (Milestone 28 / M15 provider dispatch).
 * Failures are caught silently — never break buyer flows.
 */
import { BUYER_ANALYTICS_EVENTS } from "../constants/automapAnalyticsEvents";
import { resolveEventCategory } from "../constants/buyerEventCategoryMap";
import { sanitizeAnalyticsPayload } from "../analytics/sanitize";
import { dispatchAnalyticsProviders } from "../analytics/analyticsProvider";
import { buildAttributionContext } from "../analytics/attribution";
import {
  getAttributionSessionId,
  getAttributionVisitorId,
} from "./campaignAttribution";
import { getPageAttributionContext } from "./pageAttributionContext";
import { appendBuyerAnalyticsEvent } from "./buyerAnalyticsEventStore";
import { ingestBuyerAnalyticsEvent } from "../services/automapBuyerAnalyticsApi";

export { BUYER_ANALYTICS_EVENTS };
export { sanitizeAnalyticsPayload } from "../analytics/sanitize";

function randomEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function safeAttributionSummary() {
  try {
    return buildAttributionContext();
  } catch {
    return {};
  }
}

export function getBuyerSessionContext() {
  const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
  const page = getPageAttributionContext(pagePath);
  return {
    sessionId: getAttributionSessionId(),
    visitorId: getAttributionVisitorId(),
    pagePath: page.pagePath,
    pageType: page.pageType,
    listingId: page.listingId || null,
    dealerId: page.dealerId || null,
    guideSlug: page.guideSlug || null,
    seoPageType: page.seoPageType || null,
    seoPageSlug: page.seoPageSlug || null,
  };
}

export function enrichEventWithPageContext(payload = {}) {
  const ctx = getBuyerSessionContext();
  return {
    ...ctx,
    ...payload,
    pagePath: payload.pagePath || ctx.pagePath,
    pageType: payload.pageType || ctx.pageType,
  };
}

export function enrichEventWithAttribution(payload = {}) {
  return {
    ...payload,
    attributionContext: payload.attributionContext || safeAttributionSummary(),
  };
}

export function trackBuyerEvent(eventName, payload = {}) {
  try {
    if (!eventName) return null;

    const enriched = enrichEventWithAttribution(
      enrichEventWithPageContext(sanitizeAnalyticsPayload(payload))
    );

    const event = {
      id: randomEventId(),
      eventName,
      eventCategory: resolveEventCategory(eventName),
      sessionId: enriched.sessionId,
      visitorId: enriched.visitorId,
      listingId: enriched.listingId || null,
      dealerId: enriched.dealerId || null,
      quizSessionId: enriched.quizSessionId || null,
      checklistSessionId: enriched.checklistSessionId || null,
      pagePath: enriched.pagePath,
      pageType: enriched.pageType,
      sourceComponent: enriched.sourceComponent || null,
      sourceAction: enriched.sourceAction || null,
      attributionContext: enriched.attributionContext,
      eventPayload: sanitizeAnalyticsPayload(enriched),
      createdAt: new Date().toISOString(),
    };

    appendBuyerAnalyticsEvent(event);

    dispatchAnalyticsProviders(eventName, event.eventPayload);

    if (typeof window !== "undefined" && window.__midpointTrack) {
      window.__midpointTrack(eventName, { domain: "automap_buyer_analytics", ...event.eventPayload });
    }

    ingestBuyerAnalyticsEvent(event).catch(() => {});

    return event;
  } catch {
    return null;
  }
}

export function trackPageView(pageContext = {}) {
  return trackBuyerEvent("page_view", {
    ...pageContext,
    sourceComponent: pageContext.sourceComponent || "page",
    sourceAction: "page_view",
  });
}

export function trackSearchPerformed(searchPayload = {}) {
  return trackBuyerEvent(BUYER_ANALYTICS_EVENTS.SEARCH_PERFORMED, {
    sourceComponent: searchPayload.sourceComponent || "search",
    ...searchPayload,
  });
}

export function trackFilterChanged(filterPayload = {}) {
  return trackBuyerEvent(BUYER_ANALYTICS_EVENTS.SEARCH_FILTER_CHANGED, {
    sourceComponent: filterPayload.sourceComponent || "search_filters",
    ...filterPayload,
  });
}

export function trackListingViewed(listingPayload = {}) {
  return trackBuyerEvent(BUYER_ANALYTICS_EVENTS.VEHICLE_DETAIL_VIEWED, {
    sourceComponent: listingPayload.sourceComponent || "vehicle_detail",
    ...listingPayload,
  });
}

export function trackDealerViewed(dealerPayload = {}) {
  const eventName =
    dealerPayload.pageType === "dealer_directory"
      ? BUYER_ANALYTICS_EVENTS.DEALER_DIRECTORY_VIEWED
      : BUYER_ANALYTICS_EVENTS.DEALER_PROFILE_VIEWED;
  return trackBuyerEvent(eventName, dealerPayload);
}

export function trackQuizEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, { sourceComponent: "buyer_quiz", ...payload });
}

export function trackRecommendationEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, { sourceComponent: "recommendation_results", ...payload });
}

export function trackChecklistEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, { sourceComponent: payload.sourceComponent || "checklist", ...payload });
}

export function trackContactEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, {
    sourceComponent: payload.sourceComponent || "contact",
    ...payload,
    conversionPage: payload.conversionPage || getBuyerSessionContext().pagePath,
    conversionPageType: payload.conversionPageType || getBuyerSessionContext().pageType,
  });
}

export function trackLeadEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, {
    sourceComponent: payload.sourceComponent || "lead_capture",
    ...payload,
  });
}

export function trackSeoEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, { sourceComponent: payload.sourceComponent || "seo_landing", ...payload });
}

export function trackGuideEvent(eventName, payload = {}) {
  return trackBuyerEvent(eventName, { sourceComponent: payload.sourceComponent || "guide", ...payload });
}
